// @story MSG-SYS10
// The list of details, on the screen (design 30 Oct 2026, "Campaigns in every
// module"). The words are stored as {{key}}, as they always were; the editor
// shows each detail by its plain name, [First name], so nobody types a
// variable. These helpers turn one into the other, and check WhatsApp words
// against Meta's rules as they are typed (the server checks them again
// before anything is submitted: campaignDetails.service.js).

export const VAR = /\{\{\s*([a-zA-Z_][a-zA-Z0-9_]*)\s*\}\}/g;
const PERSON_ONLY = new Set(['firstName', 'fullName', 'role']);

/* The list for a communication: Hiring's details and its own one-off ones,
   as the server builds it. An own detail with the key of a built-in one
   replaces it (a communication written before the list kept its own value). */
export function catalogueOf(builtIn = [], variables = []) {
  const own = variables.filter((v) => v && v.key && !PERSON_ONLY.has(v.key));
  const ownKeys = new Set(own.map((v) => v.key));
  return [
    ...builtIn.filter((d) => !ownKeys.has(d.key)).map((d) => ({ ...d, own: false })),
    ...own.map((v) => ({ key: v.key, label: v.label || v.key, example: v.value || '', canBeEmpty: false, own: true })),
  ];
}

// A label as it shows between brackets: no brackets inside it.
const shown = (label) => String(label || '').replace(/[[\]]/g, '').trim();

/* {{firstName}} → [First name]. A {{key}} not in the list stays as typed, and is flagged. */
export function toDisplay(value, list) {
  const byKey = new Map(list.map((d) => [d.key, d]));
  return String(value || '').replace(VAR, (m, k) => (byKey.has(k) ? `[${shown(byKey.get(k).label)}]` : m));
}

/* [First name] → {{firstName}}, for every label in the list (longest first,
   so "Interview date" is never read as part of a longer label). */
export function fromDisplay(text, list) {
  const labels = [...list].filter((d) => shown(d.label)).sort((a, b) => shown(b.label).length - shown(a.label).length);
  if (!labels.length) return String(text || '');
  const byLabel = new Map(labels.map((d) => [shown(d.label).toLowerCase(), d.key]));
  const esc = (s) => s.replace(/[.*+?^${}()|[\]\\]/g, '\\$&');
  const rx = new RegExp(`\\[(${labels.map((d) => esc(shown(d.label))).join('|')})\\]`, 'gi');
  return String(text || '').replace(rx, (m, l) => `{{${byLabel.get(l.toLowerCase())}}}`);
}

/* Where each detail sits in the shown words, for drawing it as a chip. */
export function chipsIn(display, list) {
  const labels = new Set(list.map((d) => shown(d.label).toLowerCase()).filter(Boolean));
  const out = [];
  for (const m of String(display || '').matchAll(/\[([^\]\n]+)\]/g)) {
    if (labels.has(m[1].toLowerCase())) out.push({ start: m.index, end: m.index + m[0].length });
  }
  return out;
}

export const usedIn = (value) => [...new Set([...String(value || '').matchAll(VAR)].map((m) => m[1]))];

/* {{keys}} typed by hand that are not details. */
export function unknownIn(value, list) {
  const known = new Set(list.map((d) => d.key));
  return usedIn(value).filter((k) => !known.has(k));
}

/* Meta's rules for WhatsApp words, in plain words (the same rules as the server's). */
export const WA_MAX = 1024;
export const WORDS_PER_DETAIL = 3;
export function whatsAppProblems(value, list) {
  const words = String(value || '');
  if (!words.trim()) return [];
  const byKey = new Map(list.map((d) => [d.key, d]));
  const name = (k) => `[${shown(byKey.get(k)?.label) || k}]`;
  const out = [];
  const trimmed = words.trim();
  const first = /^\{\{\s*([a-zA-Z_][a-zA-Z0-9_]*)\s*\}\}/.exec(trimmed);
  const last = /\{\{\s*([a-zA-Z_][a-zA-Z0-9_]*)\s*\}\}[\s.!?,;:]*$/.exec(trimmed);
  if (first) out.push(`It starts with ${name(first[1])}. WhatsApp does not allow a detail at the very start: begin with a word, e.g. "Dear ${name(first[1])}".`);
  if (last) out.push(`It ends with ${name(last[1])}. WhatsApp does not allow a detail at the very end: add a closing line after it, e.g. "Thank you."`);
  for (const m of words.matchAll(/\{\{\s*([a-zA-Z_][a-zA-Z0-9_]*)\s*\}\}\s*\{\{\s*([a-zA-Z_][a-zA-Z0-9_]*)\s*\}\}/g)) {
    out.push(`${name(m[1])} and ${name(m[2])} are side by side. WhatsApp needs words between two details.`);
  }
  const count = [...words.matchAll(VAR)].length;
  const plain = words.replace(VAR, ' ').split(/\s+/).filter((w) => /[\p{L}\p{N}]/u.test(w)).length;
  if (count && plain < count * WORDS_PER_DETAIL) {
    out.push(`Too many details for so few words: ${count} detail${count === 1 ? '' : 's'} and ${plain} other word${plain === 1 ? '' : 's'}. Write at least ${count * WORDS_PER_DETAIL} words, or use fewer details.`);
  }
  const numbered = words.replace(VAR, '{{0}}');
  if (numbered.length > WA_MAX) out.push(`It is ${numbered.length} characters; WhatsApp allows ${WA_MAX}. Shorten it by ${numbered.length - WA_MAX}.`);
  return out;
}

/* A name in the words for a new one-off detail, from its label: "Event
   venue" → eventVenue, never one already taken. */
export function keyFor(label, taken = []) {
  const words = String(label || '').replace(/[^A-Za-z0-9 ]+/g, ' ').trim().split(/\s+/).filter(Boolean);
  let base = words.map((w, i) => (i ? w[0].toUpperCase() + w.slice(1).toLowerCase() : w.toLowerCase())).join('') || 'detail';
  if (!/^[a-zA-Z_]/.test(base)) base = `d${base}`;
  base = base.slice(0, 36);
  const used = new Set(taken);
  if (!used.has(base)) return base;
  for (let n = 2; ; n += 1) if (!used.has(`${base}${n}`)) return `${base}${n}`;
}
